import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Autocomplete, Select, MenuItem,
  FormControl, IconButton, Tooltip, Checkbox, FormControlLabel, Divider,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useData } from '../context/DataContext';
import type { PurchaseOrder } from '../data/types';
import { BRAND, BRAND_LIGHT, BRAND_BORDER, INK, INK_SOFT } from '../theme/tokens';

/**
 * POEditForm — FieldInsight-faithful editable PO header.
 *
 * Mirrors the legacy FieldInsight Purchase Order authoring screen
 * (Supplier · Site · Project · Details grid · Notes) so the user can
 * edit every header field inline. Each field commits straight to
 * DataContext.updatePurchaseOrder — no Save button — debounced for text
 * inputs to avoid a write per keystroke.
 *
 * Companion to <POPrintPage> in v1.12.0+. Shown inside the Details
 * chevron disclosure section (v1.27.12+).
 */

const INVOICE_TERMS = ['7 Day Terms', '14 Day Terms', '30 Days EOM', '60 Days EOM', 'COD', 'Payment on Completion'];
const DELIVERY_STATUSES = ['Pending', 'Partial', 'Delivered'];
const PAYMENT_METHODS = ['EFT', 'Card', 'Cash', 'Cheque'];
const CURRENCIES = ['AUD - $', 'USD - $', 'NZD - $', 'GBP - £', 'EUR - €'];

const labelSx = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: INK_SOFT,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  mb: 0.4,
  display: 'block',
};

// Brand-purple tinted text input — matches the legacy FieldInsight
// authoring screen aesthetic from screenshot 2.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: BRAND_LIGHT,
    borderRadius: 1,
    fontSize: '0.82rem',
    '& fieldset':                  { borderColor: 'transparent' },
    '&:hover fieldset':            { borderColor: BRAND_BORDER },
    '&.Mui-focused fieldset':      { borderColor: BRAND, borderWidth: 1 },
  },
  '& .MuiInputBase-input': {
    py: 0.7,
    px: 1,
    color: INK,
    fontWeight: 500,
  },
};

// Small read-only "fact" line under each picker, e.g. address details.
const factSx = { fontSize: '0.72rem', color: INK_SOFT, lineHeight: 1.5 };

interface POEditFormProps {
  po: PurchaseOrder;
}

export const POEditForm: React.FC<POEditFormProps> = ({ po }) => {
  const { suppliers, sites, projects, updatePurchaseOrder } = useData();

  // Local form state mirrors PO. All inputs drive local state immediately
  // (no input lag) and a debounced effect commits to DataContext.
  const [draft, setDraft] = useState<PurchaseOrder>(po);

  // Re-sync if the underlying PO changes (e.g. another component updates
  // status or someone navigates between POs).
  useEffect(() => { setDraft(po); }, [po.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced commit — 400ms after last edit
  const commitTimer = useRef<number | null>(null);
  useEffect(() => {
    if (commitTimer.current) window.clearTimeout(commitTimer.current);
    // Skip the initial mount (draft == po, no diff to write)
    const drift = Object.keys(draft).some(k => (draft as any)[k] !== (po as any)[k]);
    if (!drift) return;
    commitTimer.current = window.setTimeout(() => {
      const updates: Partial<PurchaseOrder> = {};
      Object.keys(draft).forEach(k => {
        if ((draft as any)[k] !== (po as any)[k]) (updates as any)[k] = (draft as any)[k];
      });
      if (Object.keys(updates).length > 0) updatePurchaseOrder(po.id, updates);
    }, 400);
    return () => { if (commitTimer.current) window.clearTimeout(commitTimer.current); };
  }, [draft, po, updatePurchaseOrder]);

  const set = <K extends keyof PurchaseOrder>(key: K, value: PurchaseOrder[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const supplier = suppliers.find(s => s.id === draft.supplierId);
  const site     = sites.find(x => x.id === draft.siteId);
  const project  = projects.find(p => p.id === draft.projectId);

  return (
    <Box sx={{ p: 2.5, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
      {/* ── Supplier ─────────────────────────────────────────────────── */}
      <Section title="Supplier">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, maxWidth: 360 }}>
            <Autocomplete
              size="small"
              options={suppliers}
              getOptionLabel={(s) => s.name}
              value={supplier ?? null}
              onChange={(_, v) => set('supplierId', v?.id ?? '')}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search supplier" sx={inputSx} />
              )}
            />
          </Box>
          <AddInlineButton tooltip="Create new supplier" />
        </Stack>
        <FactGrid>
          <Fact label="Business name"   value={supplier?.name} />
          <Fact label="Business number" value={supplier?.abn ? `ABN ${supplier.abn}` : undefined} />
          <Fact label="Address"         value={supplier?.address} />
          <Fact label="Email"           value={supplier?.email} />
          <Fact label="Landline"        value={supplier?.phone} />
        </FactGrid>
      </Section>

      <Divider sx={{ my: 2.5 }} />

      {/* ── Site ─────────────────────────────────────────────────────── */}
      <Section title="Site">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, maxWidth: 360 }}>
            <Autocomplete
              size="small"
              options={sites}
              getOptionLabel={(s) => s.name}
              value={site ?? null}
              onChange={(_, v) => set('siteId', v?.id ?? undefined)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search site" sx={inputSx} />
              )}
            />
          </Box>
          <AddInlineButton tooltip="Create new site" />
        </Stack>
        <FactGrid>
          <Fact label="Business name" value={site?.businessName} />
          <Fact label="Email"         value={site?.email} />
          <Fact label="Address"       value={site?.address} />
        </FactGrid>
      </Section>

      <Divider sx={{ my: 2.5 }} />

      {/* ── Project ─────────────────────────────────────────────────── */}
      <Section title="Project">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, maxWidth: 360 }}>
            <Autocomplete
              size="small"
              options={projects}
              getOptionLabel={(p) => p.name}
              value={project ?? null}
              onChange={(_, v) => set('projectId', v?.id ?? undefined)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search project" sx={inputSx} />
              )}
            />
          </Box>
          <Tooltip title="Open project in new tab" arrow>
            <IconButton size="small" disabled={!project}
              sx={{ color: BRAND, '&:hover': { backgroundColor: BRAND_LIGHT },
                '&.Mui-disabled': { color: '#ccc' } }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <AddInlineButton tooltip="Create new project" />
        </Stack>
        <FactGrid>
          <Fact label="Project name"   value={project?.name} />
          <Fact label="Project number" value={project?.number} />
          <Fact label="Start date"     value={project?.startDate} />
          <Fact label="End date"       value={project?.endDate} />
        </FactGrid>
      </Section>

      <Divider sx={{ my: 2.5 }} />

      {/* ── Details grid ──────────────────────────────────────────────
        Mirrors the legacy FieldInsight 'Details' grid: 4 rows × 4
        columns of small inputs with brand-purple tint. Required
        fields marked with a brand-purple *. */}
      <Section title="Details">
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1.5,
          mb: 1.5,
        }}>
          <Field label="Title" required>
            <TextField size="small" value={draft.title ?? ''} sx={inputSx} fullWidth
              onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label="Invoice term" required>
            <FormControl size="small" fullWidth>
              <Select value={draft.invoiceTerm ?? ''} displayEmpty sx={inputSx}
                onChange={(e) => set('invoiceTerm', e.target.value)}>
                <MenuItem value=""><em>—</em></MenuItem>
                {INVOICE_TERMS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Field>
          <Field label="Delivery status">
            <FormControl size="small" fullWidth>
              <Select value={draft.deliveryStatus ?? ''} displayEmpty sx={inputSx}
                onChange={(e) => set('deliveryStatus', e.target.value)}>
                <MenuItem value=""><em>add</em></MenuItem>
                {DELIVERY_STATUSES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          </Field>

          <Field label="Delivery due date">
            <TextField size="small" type="date" sx={inputSx} fullWidth
              value={draft.deliveryDueDate ?? ''}
              onChange={(e) => set('deliveryDueDate', e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Field>
          <Field label="Purchase order date" required>
            <TextField size="small" type="date" sx={inputSx} fullWidth
              value={draft.date ?? ''}
              onChange={(e) => set('date', e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Field>
          <Field label="Delivered date">
            <TextField size="small" type="date" sx={inputSx} fullWidth
              value={draft.deliveredDate ?? ''}
              onChange={(e) => set('deliveredDate', e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Field>
          <Field label="Supplier invoice #">
            <TextField size="small" sx={inputSx} fullWidth
              value={draft.supplierInvoiceNo ?? ''}
              onChange={(e) => set('supplierInvoiceNo', e.target.value)} />
          </Field>

          <Field label="Supplier invoice date">
            <TextField size="small" type="date" sx={inputSx} fullWidth
              value={draft.supplierInvoiceDate ?? ''}
              onChange={(e) => set('supplierInvoiceDate', e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Field>
          <Field label="Payment method">
            <FormControl size="small" fullWidth>
              <Select value={draft.paymentMethod ?? ''} displayEmpty sx={inputSx}
                onChange={(e) => set('paymentMethod', e.target.value)}>
                <MenuItem value=""><em>add</em></MenuItem>
                {PAYMENT_METHODS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Field>
          <Field label="Owner / Sales rep">
            <TextField size="small" sx={inputSx} fullWidth
              value={draft.ownerSalesRep ?? ''}
              onChange={(e) => set('ownerSalesRep', e.target.value)} />
          </Field>

          <Field label="Currency">
            <FormControl size="small" fullWidth>
              <Select value={draft.currency ?? 'AUD - $'} sx={inputSx}
                onChange={(e) => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Field>
          <Field label="Division">
            <TextField size="small" sx={inputSx} fullWidth
              value={draft.division ?? ''}
              onChange={(e) => set('division', e.target.value)} />
          </Field>
          <Field label="Reference">
            <TextField size="small" sx={inputSx} fullWidth
              value={draft.reference ?? ''}
              onChange={(e) => set('reference', e.target.value)} />
          </Field>
          <Field label="Job ref">
            <TextField size="small" sx={inputSx} fullWidth
              value={draft.jobRef ?? ''}
              onChange={(e) => set('jobRef', e.target.value)} />
          </Field>
        </Box>

        {/* Sent / Labour Hire toggles */}
        <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={!!draft.sent}
              onChange={(e) => set('sent', e.target.checked)}
              sx={{ color: BRAND, '&.Mui-checked': { color: BRAND } }} />}
            label={<Typography sx={{ fontSize: '0.78rem' }}>Sent</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={!!draft.labourHire}
              onChange={(e) => set('labourHire', e.target.checked)}
              sx={{ color: BRAND, '&.Mui-checked': { color: BRAND } }} />}
            label={<Typography sx={{ fontSize: '0.78rem' }}>Labour Hire</Typography>}
          />
        </Stack>
      </Section>

      <Divider sx={{ my: 2.5 }} />

      {/* ── Notes / Address blocks ─────────────────────────────────── */}
      <Section title="Notes & Conditions">
        <Stack spacing={1.5}>
          <Field label="Internal notes">
            <TextField size="small" multiline minRows={2} sx={inputSx} fullWidth
              value={draft.internalNotes ?? draft.notes ?? ''}
              onChange={(e) => set('internalNotes', e.target.value)} />
          </Field>
          <Field label="Delivery address">
            <TextField size="small" multiline minRows={2} sx={inputSx} fullWidth
              value={draft.deliveryAddress ?? ''}
              onChange={(e) => set('deliveryAddress', e.target.value)}
              placeholder={site?.address ?? 'Defaults to selected Site address'} />
          </Field>
          <Field label="Exclusions / Inclusions / Special Conditions">
            <TextField size="small" multiline minRows={2} sx={inputSx} fullWidth
              value={draft.exclusionsInclusions ?? ''}
              onChange={(e) => set('exclusionsInclusions', e.target.value)} />
          </Field>
        </Stack>
      </Section>

      <Typography sx={{ fontSize: '0.65rem', color: INK_SOFT, mt: 2, fontStyle: 'italic' }}>
        Changes save automatically.
      </Typography>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: INK, mb: 1.2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <Box>
    <InputLabel shrink sx={{ ...labelSx, position: 'static', transform: 'none' }}>
      {label}{required && <span style={{ color: BRAND, marginLeft: 2 }}>*</span>}
    </InputLabel>
    {children}
  </Box>
);

const Fact: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'baseline' }}>
    <Typography sx={{ fontSize: '0.7rem', color: INK_SOFT, fontWeight: 600, minWidth: 110 }}>
      {label}:
    </Typography>
    <Typography sx={factSx}>
      {value && value.trim() ? value : '—'}
    </Typography>
  </Box>
);

const FactGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    columnGap: 3,
    rowGap: 0.4,
    pl: 0.5,
  }}>
    {children}
  </Box>
);

const AddInlineButton: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip} arrow>
    <IconButton size="small"
      sx={{
        backgroundColor: BRAND,
        color: '#fff',
        borderRadius: 1,
        p: 0.4,
        '&:hover': { backgroundColor: '#4a0a82' },
      }}>
      <AddIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Tooltip>
);

export default POEditForm;
